import React from 'react';
import { useTheme } from '@/context/theme.context';

export type BadgeSize = 'xs' | 'sm' | 'md' | 'lg';
export type BadgeVariant =
  | 'solid' // full gradient bg, white icon
  | 'light' // semi-transparent tinted bg, primary-colored icon
  | 'ghost' // no bg, no border — just the primary-colored icon
  | 'outline' // transparent bg + primary border, primary-colored icon
  | 'flat' // solid neutral gray bg, primary-colored icon
  | 'custom'; // fully controlled via customStyle / className

const sizeClasses: Record<BadgeSize, string> = {
  xs: 'w-6 h-6 rounded-lg',
  sm: 'w-9 h-9 rounded-xl',
  md: 'w-12 h-12 rounded-2xl',
  lg: 'w-14 h-14 rounded-2xl',
};

export interface IconBadgeProps {
  /** SVG / React element rendered inside the badge */
  icon: React.ReactNode;
  /** Badge size. Defaults to `"sm"`. */
  size?: BadgeSize;
  /**
   * Visual variant:
   * - `"solid"`   — gradient fill, white icon (default)
   * - `"light"`   — semi-transparent tinted bg, primary icon
   * - `"ghost"`   — no bg/border, just the icon in primary color
   * - `"outline"` — transparent bg + primary-colored border + primary icon
   * - `"flat"`    — neutral gray bg, primary icon
   * - `"custom"`  — use `customStyle` + `className` for full control
   */
  variant?: BadgeVariant;
  /** Force a branded drop-shadow (auto-true for `md`/`lg` solid) */
  shadow?: boolean;
  /**
   * Inline style override — merged on top of the resolved variant style.
   * Use with `variant="custom"` for full control, or any variant for tweaks.
   */
  customStyle?: React.CSSProperties;
  /** Extra Tailwind utility classes */
  className?: string;
  /**
   * Custom CSS color string (hex, rgb, Tailwind var, etc.) to override the
   * theme primary on `light`, `ghost`, `outline` variants.
   * e.g. `"rgb(22,163,74)"` for green, `"rgb(239,68,68)"` for red.
   */
  color?: string;
}

/**
 * Square icon badge with multiple visual variants and full customisation support.
 */
export const IconBadge = ({
  icon,
  size = 'sm',
  variant = 'solid',
  shadow,
  customStyle,
  className = '',
  color,
}: IconBadgeProps) => {
  const { colors } = useTheme();
  const { primary, secondary } = colors;

  // Use custom color if provided, else fall back to theme primary
  const accent = color ?? primary;

  const hasShadow =
    shadow ?? (variant === 'solid' && (size === 'md' || size === 'lg'));

  // ── Variant styles ────────────────────────────────────────────────────────
  let variantStyle: React.CSSProperties = {};
  let iconColorClass = '';

  switch (variant) {
    case 'solid':
      variantStyle = {
        background: `linear-gradient(135deg, ${primary}, ${secondary})`,
        boxShadow: hasShadow ? `0 4px 16px ${primary}60` : 'none',
      };
      iconColorClass = 'text-white';
      break;

    case 'light':
      variantStyle = {
        background: `linear-gradient(135deg, ${accent}22 0%, ${accent}44 100%)`,
        border: `1px solid ${accent}30`,
        color: accent,
      };
      break;

    case 'ghost':
      variantStyle = { color: accent };
      break;

    case 'outline':
      variantStyle = {
        background: 'transparent',
        border: `1.5px solid ${accent}60`,
        color: accent,
      };
      break;

    case 'flat':
      variantStyle = {};
      iconColorClass =
        'bg-gray-100 dark:bg-white/[0.07] text-gray-600 dark:text-gray-300';
      break;

    case 'custom':
      // fully controlled by customStyle + className
      break;
  }

  // merge customStyle on top
  const resolvedStyle = { ...variantStyle, ...customStyle };

  return (
    <div
      className={[
        sizeClasses[size],
        'flex items-center justify-center shrink-0',
        iconColorClass,
        className,
      ]
        .filter(Boolean)
        .join(' ')}
      style={resolvedStyle}
    >
      {icon}
    </div>
  );
};
